// @flow
import React from 'react';
import makeStyles from '@material-ui/core/styles/makeStyles';
import IosSwitch from '../../../../../elements/IosSwitch/IosSwitch';
import {Typography, Grid, IconButton} from '@material-ui/core';
import {colors} from '../../../../../assets/styles/colors';
import StarRating from '../StarRating/StarRating';
import {capitalize} from '../../../../../utils/string';
import {getImageUrl} from '../../../../../utils/image_provider';
import {useIsMobile} from '../../../../../utils/dom';
import {StarRounded} from '@material-ui/icons';

import type {Review, OrderItem} from '../../../../../entities';
import type {ReviewsAction} from '../../duck';
import {formatDate} from '../../../../../utils/date';

type Props = {|
  handleReviewEnabled: (payload: ReviewsAction) => void,
  review: Review,
|};

const useStyles = makeStyles(theme => ({
  wrapReview: {
    background: '#FFFFFF',
    boxShadow:
      '0px 0px 1px rgba(40, 41, 61, 0.08), 0px 0.5px 2px rgba(96, 97, 112, 0.16)',
    borderRadius: '8px',
    margin: theme.spacing(1),
    padding: theme.spacing(2),
  },
  text: {
    maxWidth: 'calc(100% - 70px)',
    fontSize: '14px',
    lineHeight: '22px',
    display: 'flex',
    alignItems: 'center',
    color: colors.grey700,
  },
  nameCustomer: {
    fontWeight: 600,
    fontSize: '16px',
    lineHeight: '24px',
    display: 'flex',
    alignItems: 'center',
    color: colors.grey800,
  },
  date: {
    fontSize: '14px',
    lineHeight: '22px',
    display: 'flex',
    alignItems: 'center',
    color: colors.grey500,
  },
  starRating: {
    margin: theme.spacing(2, 0, 1),
  },
  buttonWrap: {
    display: 'flex',
    alignItems: 'center',
  },
  featuredToggle: {
    // zIndex: 1,
    // position: 'absolute',
    right: 14,
    // bottom: 4,
    boxShadow: '0px 0px 2px 2px rgba(96, 97, 112, 0.16)',
  },
  orderItemWrap: {
    width: '100%',
    maxWidth: '64px',
    maxHeight: '64px',
    marginRight: theme.spacing(1),
  },
  squareWrap: {
    width: '100%',
    paddingBottom: '100%',
    position: 'relative',
    boxSizing: 'border-box',
  },
  squareContent: {
    position: 'absolute',
    top: 0,
    left: 0,
    bottom: 0,
    right: 0,
    height: '100%',
    width: '100%',
  },
  imageOrderItem: {
    display: 'block',
    height: '100%',
    width: '100%',
    maxWidth: '64px',
    maxHeight: '64px',
    objectFit: 'cover',
    border: `1px solid ${colors.grey300}`,
    borderRadius: theme.spacing(1),
  },
  morePhotos: {
    backgroundSize: 'cover',
    opacity: '0.8',
  },
  textMorePhotos: {
    position: 'absolute',
    top: '30%',
    left: '30%',
    color: colors.grey900,
    textAlign: 'center',
    fontWeight: 500,
    fontSize: '20px',
    lineHeight: '32px',
    [theme.breakpoints.down('sm')]: {
      top: '20%',
      left: '20%',
    },
  },
  wrapImages: {
    display: 'flex',
    height: '100%',
    width: '100%',
    maxWidth: '450px',
    marginTop: theme.spacing(1),
  },
  inLineWrap: {
    display: 'flex',
    alignItems: 'center',
  },
  sectionLabel: {
    fontSize: '12px',
    fontWeight: 600,
    color: colors.grey600,
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    marginBottom: theme.spacing(1),
    marginTop: theme.spacing(1),
  },
  reviewImagesSection: {
    width: '100%',
  },
  orderItemsSection: {
    width: '100%',
  },
}));

export default function ReviewPost(props: Props) {
  const {handleReviewEnabled, review} = props;
  const classes = useStyles();
  const maxItems = useIsMobile() ? 4 : 5;

  const maxCant =
    review.order.orderItems.length > maxItems
      ? maxItems
      : review.order.orderItems.length;

  const dateString = formatDate(review.createdAt, 'MMMM D, YYYY');
  const switchElement = (
    <div className={classes.buttonWrap}>
      <IosSwitch
        checked={review.enabled}
        onChange={() =>
          handleReviewEnabled({
            enabled: !review.enabled,
            orderId: review.order.id,
          })
        }
      />
    </div>
  );

  const switchElementFeature = (
    <IconButton
      className={classes.featuredToggle}
      onClick={() =>
        handleReviewEnabled({
          featured: !review.featured,
          orderId: review.order.id,
        })
      }
      size="small"
    >
      <StarRounded
        style={{
          color: review.featured ? '#FFC107' : colors.grey500,
        }}
      />
    </IconButton>
  );

  return (
    <Grid
      container
      className={classes.wrapReview}
      justify="center"
      alignItems="center"
    >
      <Grid
        item
        container
        xs={12}
        sm={12}
        md={12}
        className={classes.inLineWrap}
        justify="space-between"
      >
        <Typography className={classes.nameCustomer}>
          {capitalize(review.order.customer.firstName) +
            ' ' +
            capitalize(review.order.customer.lastName)}
        </Typography>
        <Typography className={classes.date}>{dateString}</Typography>
      </Grid>
      <Grid item xs={12} sm={12} md={12}>
        <StarRating
          className={classes.starRating}
          size={100}
          punctuation={Number(review.rating)}
        />
      </Grid>
      <Grid
        item
        container
        xs={12}
        sm={12}
        md={12}
        className={classes.inLineWrap}
        justify="space-between"
      >
        <Typography className={classes.text}>{review.description}</Typography>
        {switchElementFeature}
      </Grid>
      {review.images && review.images.length > 0 && (
        <Grid
          item
          xs={12}
          sm={12}
          md={12}
          className={classes.reviewImagesSection}
        >
          <Typography className={classes.sectionLabel}>
            Fotos del cliente
          </Typography>
          <div className={classes.wrapImages}>
            {review.images.slice(0, maxItems).map((image, index: number) => {
              return (
                <div
                  key={`review-image-${index}`}
                  className={classes.orderItemWrap}
                >
                  <div className={classes.squareWrap}>
                    <div className={classes.squareContent}>
                      <img
                        src={getImageUrl(image.id)}
                        alt={`Foto de reseña ${index + 1}`}
                        className={`${classes.imageOrderItem} ${classes.reviewImage}`}
                      />
                    </div>
                  </div>
                </div>
              );
            })}
            {review.images.length > maxItems && (
              <div className={classes.orderItemWrap}>
                <div className={classes.squareWrap}>
                  <div className={classes.squareContent}>
                    <div
                      className={`${classes.imageOrderItem} ${classes.morePhotos}`}
                      style={{
                        backgroundImage: `linear-gradient(to bottom, rgba(244, 244, 246,0.5), rgba(244, 244, 246,0.5)), url(
                        ${getImageUrl(review.images[maxItems].id)}
                      )`,
                      }}
                    />
                    <span className={classes.textMorePhotos}>{`+ ${review.images
                      .length - maxItems}`}</span>
                  </div>
                </div>
              </div>
            )}
          </div>
        </Grid>
      )}
      <Grid
        item
        container
        xs={12}
        sm={12}
        md={12}
        className={classes.orderItemsSection}
      >
        <Typography className={classes.sectionLabel}>
          Productos del pedido
        </Typography>
        <div
          className={classes.inLineWrap}
          style={{width: '100%', justifyContent: 'space-between'}}
        >
          <div className={classes.wrapImages}>
            {review.order.orderItems
              .slice(0, maxCant)
              .map((orderItem: OrderItem, index: number) => {
                return (
                  <div
                    key={`order-item-${index}`}
                    className={classes.orderItemWrap}
                  >
                    <div className={classes.squareWrap}>
                      <div className={classes.squareContent}>
                        <img
                          src={getImageUrl(
                            orderItem.imageId ? orderItem.imageId : null
                          )}
                          alt={orderItem.name}
                          className={classes.imageOrderItem}
                        />
                      </div>
                    </div>
                  </div>
                );
              })}
            {review.order.orderItems.length > maxItems && (
              <div className={classes.orderItemWrap}>
                <div className={classes.squareWrap}>
                  <div className={classes.squareContent}>
                    <div
                      className={`${classes.imageOrderItem} ${classes.morePhotos}`}
                      style={{
                        backgroundImage: `linear-gradient(to bottom, rgba(244, 244, 246,0.5), rgba(244, 244, 246,0.5)), url(
                        ${getImageUrl(
                          review.order.orderItems[maxItems].imageId
                            ? review.order.orderItems[maxItems].imageId
                            : null
                        )}
                      )`,
                      }}
                    />
                    <span className={classes.textMorePhotos}>{`+ ${review.order
                      .orderItems.length - maxItems}`}</span>
                  </div>
                </div>
              </div>
            )}
          </div>
          {switchElement}
        </div>
      </Grid>
    </Grid>
  );
}
